<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { RouterLink } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

import AppIcon from '@/components/ui/AppIcon.vue'
import type { IconName } from '@/lib/icons'

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success'
  | 'outline'
  | 'energy'
  | 'energy-text'
type Size = 'sm' | 'compact' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    to?: RouteLocationRaw
    href?: string
    ariaLabel?: string
    icon?: IconName
    iconRight?: IconName
    /** Keep the label centered and hang the icon just to its left. */
    iconAside?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'md',
    type: 'button',
  },
)

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-gradient-to-r from-reybex-orange to-reybex-amber text-white shadow-sm hover:brightness-105 focus-visible:outline-reybex-orange disabled:opacity-60',
  secondary:
    'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-700',
  outline:
    'bg-transparent text-brand-700 shadow-none ring-1 ring-brand-200 hover:bg-brand-50 dark:text-brand-300 dark:ring-brand-800 dark:hover:bg-brand-950',
  ghost:
    'bg-transparent text-gray-600 shadow-none hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
  info: 'bg-[#1976D2] text-white hover:bg-[#1565C0]',
  warning: 'bg-[#FFB300] text-white hover:bg-[#FFA000]',
  danger: 'bg-[#FF5252] text-white hover:bg-[#E53935]',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-300',
  energy:
    'bg-gradient-to-r from-reybex-orange to-reybex-amber text-white shadow-sm hover:brightness-105 focus-visible:outline-reybex-orange disabled:opacity-60',
  'energy-text':
    'bg-transparent text-reybex-orange shadow-none hover:bg-reybex-orange/10',
}

const SIZES: Record<Size, string> = {
  sm: 'gap-x-1.5 px-2.5 py-1.5 text-xs',
  compact: 'gap-x-1.5 px-3 py-2 text-xs',
  md: 'gap-x-2 px-4 py-2.5 text-sm',
  lg: 'gap-x-2 px-5 py-3 text-base',
}

const classes = computed(() => [
  'inline-flex cursor-pointer items-center justify-center rounded-lg font-medium shadow-sm transition-colors',
  'disabled:pointer-events-none disabled:cursor-default disabled:opacity-60',
  VARIANTS[props.variant],
  SIZES[props.size],
  props.block ? 'w-full' : '',
])

const isDisabled = computed(() => props.disabled || props.loading)

const tag = computed(() => {
  if (props.to && !isDisabled.value) return RouterLink
  if (props.href && !isDisabled.value) return 'a'
  return 'button'
})

const iconSize = computed(() => (props.size === 'sm' || props.size === 'compact' ? 'xs' : 'sm'))

const attrs = useAttrs()
const accessibleName = computed(
  () => props.ariaLabel ?? (typeof attrs['aria-label'] === 'string' ? attrs['aria-label'] : undefined),
)
</script>

<template>
  <component
    :is="tag"
    :to="to && !isDisabled ? to : undefined"
    :href="href && !isDisabled ? href : undefined"
    :rel="href && !isDisabled ? 'noopener noreferrer' : undefined"
    :type="tag === 'button' ? type : undefined"
    :class="classes"
    :disabled="tag === 'button' ? isDisabled : undefined"
    :aria-label="accessibleName"
    :aria-busy="loading || undefined"
  >
    <span class="relative inline-flex items-center" :class="iconAside ? '' : 'gap-x-1.5'">
      <AppIcon v-if="loading" name="loader" :size="iconSize" class="animate-spin" />
      <AppIcon
        v-else-if="icon"
        :name="icon"
        :size="iconSize"
        :class="iconAside ? 'absolute end-full top-1/2 me-1.5 -translate-y-1/2' : ''"
      />
      <slot v-else name="icon" />
      <slot />
    </span>
    <AppIcon v-if="iconRight && !loading" :name="iconRight" :size="iconSize" />
  </component>
</template>
